export type TeamRole = "owner" | "member";
export type ClientStatus = "Active" | "Paused";
export type Frequency = "Monthly" | "Every 2 weeks" | "Weekly" | "Once-off";
export type WorkStatus = "Planned" | "Completed" | "Cancelled";
export type PaymentStatus = "Not due" | "Unpaid" | "Part paid" | "Paid";
export type ToolPriority = "Essential" | "High" | "Expansion" | "Optional";
export type PurchaseMonth = "Existing" | "Month 1" | "Month 2" | "Month 3" | "Later";

export const activityTypes = [
  "Lawn mowing",
  "Weed cutting",
  "Edge trimming",
  "Hedge trimming",
  "Pruning",
  "Raking and clean-up",
  "Garden waste removal",
  "Soil and bed work",
  "Watering",
  "Fertiliser or compost",
  "Herbicide or pest control",
  "General home care",
  "Other",
] as const;

export type ActivityType = (typeof activityTypes)[number];

export interface CompanyProfile {
  id: string;
  company_name: string;
  trading_name: string;
  registration_number: string;
  registration_date: string;
  region: string;
  currency_code: string;
  updated_at?: string;
}

export interface TeamMember {
  user_id: string;
  display_name: string;
  role: TeamRole;
  active: boolean;
  created_at?: string;
  updated_at?: string;
}

export interface Client {
  id: string;
  client_name: string;
  status: ClientStatus;
  amount_per_payment: number;
  frequency: Frequency;
  confirmed: boolean;
  notes: string;
  sort_order: number;
  created_by?: string;
  created_at?: string;
  updated_at?: string;
}

export interface WorkActivity {
  id: string;
  work_date: string;
  team_member_id: string;
  client_id: string | null;
  activity_type: ActivityType;
  description: string;
  start_time: string | null;
  end_time: string | null;
  duration_minutes: number;
  status: WorkStatus;
  amount_charged: number;
  amount_received: number;
  payment_status: PaymentStatus;
  petrol_litres: number;
  two_stroke_oil_ml: number;
  trimmer_line_cost: number;
  refuse_bags_cost: number;
  maintenance_cost: number;
  materials_cost: number;
  transport_cost: number;
  phone_data_cost: number;
  other_cost: number;
  tools_used: string[];
  notes: string;
  created_by?: string;
  created_at?: string;
  updated_at?: string;
}

export type WorkActivityInput = Omit<
  WorkActivity,
  "id" | "created_by" | "created_at" | "updated_at"
>;

export interface MonthlyCost {
  id: string;
  category: string;
  expense: string;
  driver_basis: string;
  quantity_per_month: number;
  unit: string;
  unit_cost: number;
  treatment: "Business" | "Personal withdrawal";
  notes: string;
  sort_order: number;
  active: boolean;
  updated_at?: string;
}

export interface ToolItem {
  id: string;
  category: string;
  tool_name: string;
  purpose: string;
  priority: ToolPriority;
  quantity: number;
  estimated_unit_cost: number;
  included: boolean;
  already_owned: boolean;
  purchase_month: PurchaseMonth;
  useful_life_months: number;
  notes: string;
  source_id: string;
  sort_order: number;
  updated_at?: string;
}

export interface BudgetAssumption {
  key: string;
  assumption: string;
  value_numeric: number | null;
  value_text: string | null;
  unit: string;
  notes: string;
  sort_order: number;
  updated_at?: string;
}

export interface SourceLog {
  source_code: string;
  item: string;
  source_value: string;
  unit: string;
  as_of: string | null;
  source_type: string;
  source_url: string | null;
  notes: string;
  sort_order: number;
}

export interface ClientMetrics {
  averagePayments: number;
  averageMonthlyIncome: number;
  normalPayments: number;
  normalMonthlyIncome: number;
}

export interface CostLine extends MonthlyCost {
  calculatedQuantity: number;
  calculatedUnitCost: number;
  monthlyCost: number;
}

export interface BudgetSnapshot {
  averageRecurringIncome: number;
  normalMonthIncome: number;
  averageVisits: number;
  cashOperatingCosts: number;
  operatingContingency: number;
  equipmentReserve: number;
  businessAllocation: number;
  personalWithdrawals: number;
  spendableMonthlyCash: number;
  starterToolsToBuy: number;
  averageRevenuePerVisit: number;
}

export interface CashPlanMonth {
  month: string;
  phase: string;
  recurringIncome: number;
  operatingCosts: number;
  contingency: number;
  equipmentReserve: number;
  personalWithdrawals: number;
  starterToolPurchases: number;
  netMovement: number;
  openingCash: number;
  closingCash: number;
  cumulativeReserve: number;
}

export interface AppData {
  company: CompanyProfile | null;
  team: TeamMember[];
  clients: Client[];
  activities: WorkActivity[];
  monthlyCosts: MonthlyCost[];
  tools: ToolItem[];
  assumptions: BudgetAssumption[];
  sources: SourceLog[];
}
